import {
  ADDRESS_SOURCE_DISPLAY,
  BILL_GROUP_INVOICE_FREQUENCY_MAP,
  BillGroupSummary,
  BillGroupSummaryListUi,
  CustomFieldEntityEnum,
  ICustomFieldResSchema,
  NET_TERMS_DISPLAY,
} from '@monetize/types/app';
import {
  DEFAULT_PAGER,
  FilterTypeOperator,
  TDataTablePager,
} from '@monetize/types/common';
import {
  adjustTimeZoneOffsetWithUtc,
  parseDateAsUtc,
} from '@monetize/utils/core';
import {
  AppColumnDef,
  createColumnHelper,
} from '../../../components/Monetize/DataTableNew/appTable';
import { addDays } from 'date-fns/addDays';
import { formatISO } from 'date-fns/formatISO';
import { isBefore } from 'date-fns/isBefore';
import { useEffect, useState } from 'react';
import { useInvokeManualBillRun } from '../../../api/accountsService';
import { handleApiErrorToast } from '../../../api/axios';
import { useGetBillGroupsForBillRun } from '../../../api/billGroupService';
import { SELECTION_COL } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import {
  addressWithContactBodyTemplate,
  dateBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getCustomFieldsColumnGroup,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import {
  AccountLookupPopover,
  BillGroupLookupPopover,
} from '../../../components/Monetize/LookupPopover';
import { useCustomFields } from '../../../hooks/useCustomFields';
import { useToast } from '../../../services/toast';
import {
  buildFilterParamsRequestObject,
  deDuplicateRecords,
} from '../../../utils';

const TODAY = adjustTimeZoneOffsetWithUtc(new Date());

const columnHelper = createColumnHelper<BillGroupSummaryListUi>();

const COLUMNS_BASE: AppColumnDef<any, any>[] = [
  {
    ...SELECTION_COL,
  },
  {
    id: 'billGroupDetails',
    header: 'Bill Group Details',
    columns: columnHelper.columns([
      columnHelper.accessor('accountName', {
        ...getColHeaderWithSize('Account', 250),
        ...getColHeaderFilters('SET'),
        cell: ({ row }) => (
          <AccountLookupPopover
            value={row.original.accountId}
            displayLabel={row.original.accountName}
          />
        ),
        enableSorting: true,
        sortFn: 'text',
      }),
      columnHelper.accessor('name', {
        ...getColHeaderWithSize('Bill Group', 250),
        ...getColHeaderFilters('SET'),
        cell: ({ row }) => (
          <BillGroupLookupPopover
            value={row.original.id}
            displayLabel={row.original.name}
          />
        ),
      }),
      columnHelper.accessor('nextInvoiceDate', {
        ...getColHeaderWithSize('Next Invoice Date'),
        ...getColHeaderFilters('DATE'),
        enableSorting: true,
        cell: dateBodyTemplate(),
        sortFn: 'alphanumeric',
      }),
      columnHelper.accessor('invoiceFrequencyLabel', {
        ...getColHeaderWithSize('Invoicing Frequency'),
        ...getColHeaderFilters('SET'),
        cell: textBodyTemplate(),
        enableSorting: false,
      }),
      columnHelper.accessor('netTerms', {
        ...getColHeaderWithSize('Net Terms'),
        ...getColHeaderFilters('SET'),
        cell: enumDisplayBodyTemplate({
          displayMap: NET_TERMS_DISPLAY,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
      columnHelper.accessor('emailCustomerInvoice', {
        ...getColHeaderWithSize('Email Customer Invoice'),
        ...getColHeaderFilters('SET'),
        cell: textBodyTemplate(),
        enableSorting: true,
        sortFn: 'text',
      }),
    ]),
  },
  {
    id: 'addresses',
    header: 'Address Information',
    columns: columnHelper.columns([
      columnHelper.accessor('billingAddress', {
        ...getColHeaderWithSize('Billing', 250),
        cell: addressWithContactBodyTemplate<BillGroupSummaryListUi>({
          contactProp: 'billingContact',
        }),
        enableSorting: false,
      }),
      columnHelper.accessor('shippingAddress', {
        ...getColHeaderWithSize('Shipping', 250),
        cell: addressWithContactBodyTemplate<BillGroupSummaryListUi>({
          contactProp: 'shippingContact',
        }),
        enableSorting: false,
      }),
      columnHelper.accessor('addressSource', {
        ...getColHeaderWithSize('Address Source', 180),
        ...getColHeaderFilters('SET'),
        cell: enumDisplayBodyTemplate({
          displayMap: ADDRESS_SOURCE_DISPLAY,
        }),
        enableSorting: true,
        sortFn: 'text',
      }),
    ]),
  },
  {
    id: 'customerValues',
    header: 'Customer Values',
    columns: columnHelper.columns([
      columnHelper.accessor('purchaseOrderNumber', {
        ...getColHeaderWithSize('PO Number'),
        ...getColHeaderFilters('SET'),
        cell: textBodyTemplate(),
        enableSorting: true,
        sortFn: 'text',
      }),
      columnHelper.accessor('registrationNumber', {
        ...getColHeaderWithSize('Registration Number'),
        ...getColHeaderFilters('SET'),
        cell: textBodyTemplate(),
        enableSorting: true,
        sortFn: 'text',
      }),
      columnHelper.accessor('vatNumber', {
        ...getColHeaderWithSize('VAT Number'),
        ...getColHeaderFilters('SET'),
        cell: textBodyTemplate(),
        enableSorting: true,
        sortFn: 'text',
      }),
    ]),
  },
];

const PAGER: TDataTablePager = {
  ...DEFAULT_PAGER,
  rows: 100,
};

/**
 * Narrows the fetched bill groups to those eligible for a bill run and
 * pre-computes composite display fields.
 *
 * Must stay at module scope: React Query only memoizes `select` when the
 * function reference is stable, so an inline arrow would re-run this over
 * every row on every render of the consuming component.
 */
export const selectEligibleBillGroupsForBillRun = (
  billGroups: BillGroupSummary[],
): BillGroupSummaryListUi[] => {
  const now = new Date();
  // If the bill run is processing bill groups when reload is triggered,
  // the same bill group can be returned multiple times if it changed between requests.
  return deDuplicateRecords(billGroups, 'id')
    .filter(
      ({ nextInvoiceDate, invoiceDelay }) =>
        !!nextInvoiceDate &&
        isBefore(
          addDays(parseDateAsUtc(nextInvoiceDate), invoiceDelay || 0),
          now,
        ),
    )
    .map((billGroup): BillGroupSummaryListUi => ({
      ...billGroup,
      emailCustomerInvoice: billGroup.autoEmailInvoice
        ? 'Automatically'
        : 'Manually',
      // Transforming in advance to simplify display and filtering since display is a composite of two fields
      invoiceFrequencyLabel: !billGroup.invoicingFrequency
        ? 'None'
        : BILL_GROUP_INVOICE_FREQUENCY_MAP[billGroup.invoicingFrequency](
            billGroup.invoicingFrequencyInMonths,
          ).label,
    }));
};

function getColumnsFromCustomFields(
  customFields: ICustomFieldResSchema[] = [],
) {
  if (!customFields || customFields.length === 0) {
    return COLUMNS_BASE;
  }
  const newColumns = [...COLUMNS_BASE];
  const accountCustomFields = customFields.filter(
    (field) => field.entity === CustomFieldEntityEnum.ACCOUNT,
  );
  const billGroupCustomFields = customFields.filter(
    (field) => field.entity === CustomFieldEntityEnum.BILL_GROUP,
  );
  if (billGroupCustomFields.length) {
    newColumns.push(
      getCustomFieldsColumnGroup({
        id: 'billGroup.customFields',
        header: 'Bill Group Custom Fields',
        accessorPathPrefix: 'customFields',
        customFieldMetadata: billGroupCustomFields,
      }),
    );
  }
  if (accountCustomFields.length) {
    newColumns.push(
      getCustomFieldsColumnGroup({
        id: 'account.customFields',
        header: 'Account Custom Fields',
        accessorPathPrefix: 'accountCustomFields',
        customFieldMetadata: accountCustomFields,
      }),
    );
  }
  return newColumns;
}

export const useManualBillRun = () => {
  const [progress, setProgress] = useState(0);
  const { addToast } = useToast();
  // Server side filters to fetch data
  const [filters, setFilters] = useState(
    buildFilterParamsRequestObject([
      {
        key: 'nextInvoiceDate',
        value: { max: formatISO(TODAY, { representation: 'date' }), min: '' },
        operator: FilterTypeOperator.GLTE,
      },
      {
        key: 'status',
        value: 'ACTIVE',
        operator: FilterTypeOperator.EQUAL,
      },
    ]),
  );

  const customFieldQueryResult = useCustomFields(
    [CustomFieldEntityEnum.ACCOUNT, CustomFieldEntityEnum.BILL_GROUP],
    true,
    true,
    {
      refetchOnWindowFocus: false,
    },
  );

  const [columns, setColumns] = useState<AppColumnDef<any, any>[]>(() =>
    getColumnsFromCustomFields(customFieldQueryResult.customFieldList),
  );

  useEffect(() => {
    if (customFieldQueryResult.customFieldList) {
      setColumns(
        getColumnsFromCustomFields(customFieldQueryResult.customFieldList),
      );
    }
  }, [customFieldQueryResult.customFieldList]);

  const billGroupQueryResult = useGetBillGroupsForBillRun(
    {
      config: PAGER,
      filters,
      onProgress: setProgress,
    },
    {
      select: selectEligibleBillGroupsForBillRun,
      refetchOnWindowFocus: false,
    },
  );

  const { mutateAsync: invokeManualBillRun, isPending: batchInitIsLoading } =
    useInvokeManualBillRun({
      onSuccess: () => {
        addToast({
          summary: `Bill run submitted successfully`,
          detail: `This can take a few minutes to complete. Reload the results to view changes.`,
          severity: 'success',
        });
      },
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });

  const handleInvokeManualBillRun = async (billGroupIds: string[]) => {
    try {
      await invokeManualBillRun({ billGroupIds });
    } catch (ex) {
      handleApiErrorToast(ex);
    }
  };

  return {
    customFieldQueryResult,
    billGroupQueryResult,
    columns,
    progress,
    handleInvokeManualBillRun,
    batchInitIsLoading,
  };
};
